import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MoreVertical, Plus, Eye, Pencil, Copy, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import {
  useDeleteTemplate,
  useDuplicateTemplate,
  useInstantiateTemplate,
} from '../../hooks/useTemplateMutations';
import { TemplatePreviewDialog } from './TemplatePreviewDialog';
import type { TemplateSummary } from '../../types';

interface TemplateRowActionsMenuProps {
  template: TemplateSummary;
}

/** Dropdown-only counterpart to `FormRowActionsMenu`, for template rows in the merged forms+templates list. */
export function TemplateRowActionsMenu({ template }: TemplateRowActionsMenuProps) {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const instantiate = useInstantiateTemplate(template.id);
  const duplicate = useDuplicateTemplate(template.id);
  const deleteTemplate = useDeleteTemplate(template.id);

  async function handleUse() {
    try {
      const { data: created } = await instantiate.mutateAsync();
      navigate(`/${orgId}/forms/${created.id}`);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  async function handleDelete() {
    const { confirmed } = await confirm({
      title: t('templates.gallery.confirmDeleteTitle'),
      description: t('templates.gallery.confirmDeleteDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await deleteTemplate.mutateAsync();
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  async function handleDuplicate() {
    try {
      await duplicate.mutateAsync(undefined);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('list.table.actions')}
                data-testid={`forms-template-row-actions-menu-${template.id}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('list.table.actions')}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleUse} disabled={instantiate.isPending}>
            <Plus className="h-4 w-4 mr-2" />
            {t('templates.gallery.useTemplate')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setPreviewOpen(true)}>
            <Eye className="h-4 w-4 mr-2" />
            {t('templates.gallery.preview')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`/${orgId}/forms/templates/${template.id}`)}>
            <Pencil className="h-4 w-4 mr-2" />
            {t('templates.gallery.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDuplicate} disabled={duplicate.isPending}>
            <Copy className="h-4 w-4 mr-2" />
            {t('templates.gallery.duplicate')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteTemplate.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('templates.gallery.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <TemplatePreviewDialog
        templateId={template.id}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />

      <ConfirmDialog
        open={isOpen}
        onOpenChange={(v) => {
          if (!v) handleCancel();
        }}
        title={options?.title ?? ''}
        description={options?.description}
        variant={options?.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
